if (!customElements.get('wv-offer-selector')) {
  customElements.define(
    'wv-offer-selector',
    class WvOfferSelector extends HTMLElement {
      constructor() {
        super();

        const dataScript = this.querySelector('[data-wv-offer-data]');
        if (!dataScript) return;

        try {
          this.data = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('wv-offer-selector: invalid data', e);
          return;
        }

        this.cardButtons = Array.from(this.querySelectorAll('.wv-offer__card'));
        this.swatchButtons = Array.from(this.querySelectorAll('.wv-offer__swatch[data-colour]'));
        this.bedGroup = this.querySelector('[data-wv-bed-group]');
        this.bedButtons = Array.from(this.querySelectorAll('.wv-offer__swatch[data-bed-colour]'));
        // Offer-dependent note text appears both in the brief line inside this
        // element and in the details disclosure below Add to cart.
        this.noteLaunchEls = Array.from(document.querySelectorAll('[data-wv-note-launch]'));
        this.noteFullsetEls = Array.from(document.querySelectorAll('[data-wv-note-fullset]'));
        this.priceEl = this.data.priceSelector ? document.querySelector(this.data.priceSelector) : null;

        this.form = document.getElementById(this.data.productFormId);
        this.variantInput = this.form ? this.form.querySelector('[name="id"]') : null;
        this.submitButton = this.form ? this.form.querySelector('[type="submit"]') : null;
        this.submitLabel = this.submitButton ? this.submitButton.querySelector('span') : null;

        // Bed-note paragraph inside the "details" disclosure below Add to cart.
        this.bedNoteEls = Array.from(document.querySelectorAll('[data-wv-bed-note]'));

        this.selectedOffer = 'launch';
        const firstAvailable = this.swatchButtons.find((btn) => !btn.disabled);
        this.selectedColour = firstAvailable ? firstAvailable.dataset.colour : null;
        this.selectedBed = null;

        this.cardButtons.forEach((btn) => {
          btn.addEventListener('click', () => this.selectOffer(btn.dataset.offer));
        });

        this.swatchButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.disabled) return;
            this.selectColour(btn.dataset.colour);
          });
        });

        this.bedButtons.forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.disabled) return;
            this.selectBed(btn.dataset.bedColour);
          });
        });

        const bowlLink = this.querySelector('[data-wv-bowl-link]');
        if (bowlLink) {
          bowlLink.addEventListener('click', (event) => {
            const target = document.querySelector(bowlLink.getAttribute('href'));
            if (!target) return;
            event.preventDefault();
            if (target.tagName === 'DETAILS') target.open = true;
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }

        const root = this.closest('.product__info-container') || document.querySelector('.product');
        if (root) root.classList.add('wv-offer-selector-active');

        this.render();
      }

      selectOffer(offer) {
        if (offer !== 'launch' && offer !== 'fullset') return;
        this.selectedOffer = offer;
        this.render();
      }

      selectColour(colour) {
        this.selectedColour = colour;
        this.render();
      }

      selectBed(bed) {
        this.selectedBed = bed;
        this.render();
      }

      currentOfferData() {
        return this.data[this.selectedOffer];
      }

      hasBedDimension() {
        const fullset = this.data.fullset;
        return !!(fullset && fullset.bedColours && fullset.bedColours.length);
      }

      // Whether a bowl colour has any purchasable variant under the current
      // offer (for the Full Set that means: with at least one bed colour).
      colourHasAnyVariant(offerData, colour) {
        if (!offerData || !offerData.variants) return false;
        if (this.selectedOffer === 'fullset' && this.hasBedDimension()) {
          return this.data.fullset.bedColours.some(
            (bed) => offerData.variants[colour + '|' + bed]
          );
        }
        return !!offerData.variants[colour];
      }

      bedVariantId(colour, bed) {
        const fullset = this.data.fullset;
        if (!fullset || !fullset.variants) return null;
        return fullset.variants[colour + '|' + bed] || null;
      }

      resolveVariantId(offerData) {
        if (!offerData || !this.selectedColour) return null;
        if (this.selectedOffer === 'fullset' && this.hasBedDimension()) {
          if (!this.selectedBed) return null;
          return this.bedVariantId(this.selectedColour, this.selectedBed);
        }
        return offerData.variants[this.selectedColour] || null;
      }

      render() {
        this.cardButtons.forEach((btn) => {
          const isSelected = btn.dataset.offer === this.selectedOffer;
          btn.classList.toggle('wv-offer__card--selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        const offerData = this.currentOfferData();
        const isFullset = this.selectedOffer === 'fullset';
        const bedActive = isFullset && this.hasBedDimension();

        // Bowl swatch availability for the selected offer.
        this.swatchButtons.forEach((btn) => {
          const colour = btn.dataset.colour;
          const availableForOffer = offerData && this.colourHasAnyVariant(offerData, colour);
          btn.disabled = !availableForOffer;
        });

        // Fall back to the first available bowl colour when the current one
        // is unavailable under the newly selected offer.
        if (offerData && (!this.selectedColour || !this.colourHasAnyVariant(offerData, this.selectedColour))) {
          const fallback = this.swatchButtons.find((btn) => !btn.disabled);
          this.selectedColour = fallback ? fallback.dataset.colour : null;
        }

        this.swatchButtons.forEach((btn) => {
          const swatchSelected = btn.dataset.colour === this.selectedColour;
          btn.classList.toggle('wv-offer__swatch--selected', swatchSelected);
          btn.setAttribute('aria-pressed', swatchSelected ? 'true' : 'false');
        });

        // Bed colour selector: only visible (and only relevant) for the Full Set.
        if (this.bedGroup) this.bedGroup.hidden = !bedActive;
        if (bedActive) {
          this.bedButtons.forEach((btn) => {
            const bed = btn.dataset.bedColour;
            btn.disabled = !(this.selectedColour && this.bedVariantId(this.selectedColour, bed));
          });

          // Default to the first available bed colour; also recover when the
          // stored choice is unavailable for the current bowl colour.
          if (!this.selectedBed || !(this.selectedColour && this.bedVariantId(this.selectedColour, this.selectedBed))) {
            const bedFallback = this.bedButtons.find((btn) => !btn.disabled);
            this.selectedBed = bedFallback ? bedFallback.dataset.bedColour : null;
          }

          this.bedButtons.forEach((btn) => {
            const bedSelected = btn.dataset.bedColour === this.selectedBed;
            btn.classList.toggle('wv-offer__swatch--selected', bedSelected);
            btn.setAttribute('aria-pressed', bedSelected ? 'true' : 'false');
          });
        }

        // Shipping note wording follows the selected offer.
        this.noteLaunchEls.forEach((el) => {
          el.hidden = isFullset;
        });
        this.noteFullsetEls.forEach((el) => {
          el.hidden = !isFullset;
        });
        this.bedNoteEls.forEach((el) => {
          el.hidden = !isFullset;
        });

        if (this.priceEl && offerData) {
          this.priceEl.textContent = offerData.priceFormatted;
        }

        const variantId = this.resolveVariantId(offerData);

        if (this.variantInput) {
          if (variantId) {
            this.variantInput.value = variantId;
            this.variantInput.disabled = false;
          } else {
            this.variantInput.disabled = true;
          }
        }

        if (this.submitButton) {
          this.submitButton.toggleAttribute('disabled', !variantId);
        }

        if (this.submitLabel && offerData) {
          if (variantId) {
            this.submitLabel.textContent = 'Add to cart — ' + offerData.priceFormatted;
          } else if (bedActive && this.selectedColour && !this.selectedBed) {
            this.submitLabel.textContent = 'Choose a bed colour';
          } else {
            this.submitLabel.textContent = 'Choose an available bowl colour';
          }
        }
      }
    }
  );
}
